import "bootstrap/dist/css/bootstrap.min.css";
import React, { Suspense, lazy, useCallback } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import Dashboard from "./components/Dashboard";
import LoadingSpinner from "./components/LoadingSpinner";
import Login from "./components/Login";
import PendingApprovalPage from "./components/PendingApprovalPage";
import TopNav from "./components/TopNav";
import { AddressProvider } from "./contexts/AddressContext";
import { useAuth } from "./contexts/AuthContext";
import { useWebSocket } from "./hooks/useWebSocket";

// Lazy-loaded components for code splitting
const MapView = lazy(() => import("./components/MapView"));
const AddressDetail = lazy(() => import("./components/AddressDetail"));
const PersonDetail = lazy(() => import("./components/PersonDetail"));
const AdminSearchPage = lazy(() => import("./pages/AdminSearchPage"));
const AdminUserManagementPage = lazy(
  () => import("./pages/AdminUserManagementPage")
);
const AdminAssignmentsPage = lazy(() => import("./pages/AdminAssignmentsPage"));
const AdminCutTurfPage = lazy(() => import("./pages/AdminCutTurfPage"));
const AdminContactManagementPage = lazy(
  () => import("./pages/AdminContactManagementPage")
);
const AdminTagsPage = lazy(() => import("./pages/AdminTagsPage"));
const AdminActivityLogPage = lazy(() => import("./pages/AdminActivityLogPage"));
const TagApplicationsPage = lazy(() => import("./pages/TagApplicationsPage"));
const AssignmentsListPage = lazy(() => import("./pages/AssignmentsListPage"));
const AssignmentDetailPage = lazy(() => import("./pages/AssignmentDetailPage"));
const FindMyFriendsPage = lazy(() => import("./pages/FindMyFriendsPage"));
const PostcardCampaignListPage = lazy(
  () => import("./pages/PostcardCampaignListPage")
);
const PostcardCampaignRecipientsPage = lazy(
  () => import("./pages/PostcardCampaignRecipientsPage")
);
const AdminPostcardManagementPage = lazy(
  () => import("./pages/AdminPostcardManagementPage")
);
const AdminDonorsPage = lazy(() => import("./pages/AdminDonorsPage"));
const AdminDonorMapPage = lazy(() => import("./pages/AdminDonorMapPage"));

function PostcardRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== "admin" && user?.role !== "postcard_writer") {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AuthenticatedApp() {
  const { user } = useAuth();
  return (
    <AddressProvider>
      <TopNav />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route
            path="/"
            element={
              user?.role === "postcard_writer" ? (
                <Navigate to="/postcard" replace />
              ) : (
                <Dashboard />
              )
            }
          />
          <Route path="/address/:addressId" element={<AddressDetail />} />
          <Route path="/map" element={<MapView />} />
          <Route path="/person/:stateId" element={<PersonDetail />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/assignments" element={<AssignmentsListPage />} />
          <Route
            path="/assignments/:assignmentName"
            element={<AssignmentDetailPage />}
          />
          <Route path="/find-my-friends" element={<FindMyFriendsPage />} />
          <Route
            path="/postcard"
            element={
              <PostcardRoute>
                <PostcardCampaignListPage />
              </PostcardRoute>
            }
          />
          <Route
            path="/postcard/:campaignId"
            element={
              <PostcardRoute>
                <PostcardCampaignRecipientsPage />
              </PostcardRoute>
            }
          />
          <Route
            path="/admin/search"
            element={
              <AdminRoute>
                <AdminSearchPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/user-management"
            element={
              <AdminRoute>
                <AdminUserManagementPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/assignments"
            element={
              <AdminRoute>
                <AdminAssignmentsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/cut-turf"
            element={
              <AdminRoute>
                <AdminCutTurfPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/contacts"
            element={
              <AdminRoute>
                <AdminContactManagementPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/tags"
            element={
              <AdminRoute>
                <AdminTagsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/tags/:tagId/applications"
            element={
              <AdminRoute>
                <TagApplicationsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/activity-log"
            element={
              <AdminRoute>
                <AdminActivityLogPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/postcard-management"
            element={
              <AdminRoute>
                <AdminPostcardManagementPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/donors"
            element={
              <AdminRoute>
                <AdminDonorsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/donors/map"
            element={
              <AdminRoute>
                <AdminDonorMapPage />
              </AdminRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <div className="container mt-4">
                <h2>Settings</h2>
                <p className="text-muted">Coming soon...</p>
              </div>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AddressProvider>
  );
}

// Listens for USER_ROLE_UPDATED WebSocket events and refreshes the current user's
// role/assignments from DynamoDB if their email matches.
function RoleUpdateListener() {
  const { user, refreshUser } = useAuth();

  const onMessage = useCallback(
    async (message: any) => {
      if (
        message.type === "USER_ROLE_UPDATED" &&
        message.data?.email === user?.email
      ) {
        console.log("Role updated by admin, refreshing user...");
        await refreshUser();
      }
    },
    [user?.email, refreshUser]
  );

  useWebSocket({ onMessage, enabled: !!user });

  return null;
}

function App() {
  const { isAuthenticated, isPending, loading } = useAuth();

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <RoleUpdateListener />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      {isPending ? (
        <PendingApprovalPage />
      ) : isAuthenticated ? (
        <AuthenticatedApp />
      ) : (
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      )}
    </BrowserRouter>
  );
}

export default App;
